/* eslint-disable react/jsx-filename-extension */
import React, { createContext, useReducer } from 'react';
import dayjs from 'dayjs';
import { set } from 'dot-prop-immutable';

const getInitialData = () => {
    const localUserData = window.localStorage.getItem('user');
    const localTeamData = window.localStorage.getItem('team');
    const localOpponentTeamData = window.localStorage.getItem('opponent');
    const localMatchupPitcher = window.localStorage.getItem('matchupSelectedPitcher');
    const localConfigData = window.localStorage.getItem('config');
    const localPlayerSettings = window.localStorage.getItem('playerSettings');
    const expiresOn = window.localStorage.getItem('expiration');

    let user = null;
    let config = null;
    let playerSettings = null;
    let team = null;
    let opponent = null;
    let matchupSelectedPitcher = null;
    try {
        if (localUserData && expiresOn && dayjs().isBefore(expiresOn)) {
            user = JSON.parse(localUserData);
            config = JSON.parse(localConfigData);
        }
    } finally {
        if (!user) {
            window.localStorage.removeItem('user');
            window.localStorage.removeItem('config');
            window.localStorage.removeItem('expiration');
        }
    }

    try {
        if (localTeamData && localTeamData !== undefined) {
            team = JSON.parse(localTeamData);
        }
    } finally {
        if (!team) {
            window.localStorage.removeItem('team');
        }
    }

    try {
        if (localOpponentTeamData && localOpponentTeamData !== undefined) {
            opponent = JSON.parse(localOpponentTeamData);
        }
    } finally {
        if (!opponent) {
            window.localStorage.removeItem('opponent');
        }
    }

    try {
        if (localMatchupPitcher && localMatchupPitcher !== undefined) {
            matchupSelectedPitcher = JSON.parse(localMatchupPitcher);
        }
    } finally {
        if (!matchupSelectedPitcher) window.localStorage.removeItem('matchupSelectedPitcher');
    }

    try {
        if (localPlayerSettings && localPlayerSettings !== undefined) {
            playerSettings = JSON.parse(localPlayerSettings);
        }
    } finally {
        if (!playerSettings) {
            window.localStorage.removeItem('playerSettings');
        }
    }

    return { user, config, playerSettings, team, opponent, matchupSelectedPitcher };
};

const setDugoutInitialContext = (user, team, opponent) => {
    const { _user, _team, _opponent } = getInitialData();
    if (!_user) {
        window.localStorage.setItem('user', JSON.stringify(user));
        window.localStorage.setItem('expiration', dayjs().add(1, 'h').toISOString());
    }
    if (!_team) {
        window.localStorage.setItem('team', JSON.stringify(team));
    }
    if (!_opponent) {
        window.localStorage.setItem('opponent', JSON.stringify(opponent));
    }
};

const initialState = getInitialData();
const store = createContext(initialState);
const { Provider } = store;

const actions = {
    LOG_IN: 'LOGIN',
    LOG_OUT: 'LOGOUT',
    SELECT_ALIAS: 'SELECTALIAS',
    ASSIGN_USER_PLAYER: 'ASSIGNUSERPLAYER',
    SET_CONFIG: 'SETCONFIG',
    SET_PLAYER_SETTINGS: 'SETPLAYERSETTINGS',
    SET_PLAYER_RMB: 'SETPLAYERRMB',
    SET_TEAM: 'SETTEAM',
    SET_OPPONENT: 'SETOPPNENT',
    SET_MATCHUP_SELECTED_PITCHER: 'SETMATCHUPPITCHER',
};

const reducer = (state, { type, data }) => {
    switch (type) {
    case actions.LOG_IN: {
        if (data?.UserName && state?.user?.UserName !== data?.UserName) {
            fetch('/api/usagetracking', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Host: window.location.hostname,
                    Type: 'login',
                    UserName: data?.UserName,
                }),
            });
        }
        return set(state, 'user', (user) => ({ ...user, ...data }));
    }
    case actions.LOG_OUT: {
        return set(set(state, 'playerSettings', null), 'user', null);
    }
    case actions.SELECT_ALIAS: {
        return set(set(state, 'playerSettings', null), 'user.alias', data);
    }
    case actions.ASSIGN_USER_PLAYER: {
        return set(state, 'user.player', data);
    }
    case actions.SET_CONFIG: {
        return set(state, 'config', data);
    }
    case actions.SET_PLAYER_SETTINGS: {
        return set(state, 'playerSettings', data);
    }
    case actions.SET_PLAYER_RMB: {
        return set(state, 'playerRmb', data); // roster management board
    }
    case actions.SET_TEAM: {
        return set(state, 'team', { ...data });
    }
    case actions.SET_OPPONENT: {
        return set(state, 'opponent', { ...data });
    }
    case actions.SET_MATCHUP_SELECTED_PITCHER: {
        return set(state, 'matchupSelectedPitcher', { ...data });
    }
    default: {
        return state;
    }
    }
};

// Save changes to local storage
const middleware = (reduce) => (state, action) => {
    const result = reduce(state, action);
    try {
        window.localStorage.setItem('user', JSON.stringify(result.user));
        window.localStorage.setItem('team', JSON.stringify(result.team));
        window.localStorage.setItem('opponent', JSON.stringify(result.opponent));
        window.localStorage.setItem('matchupSelectedPitcher', JSON.stringify(result.matchupSelectedPitcher));
        window.localStorage.setItem('config', JSON.stringify(result.config));
        window.localStorage.setItem('playerSettings', JSON.stringify(result.playerSettings));
        window.localStorage.setItem('playerRmb', JSON.stringify(result.playerRmb));
        window.localStorage.setItem('expiration', dayjs().add(12, 'h').toISOString());
    } catch { /* silently fail */ }
    return result;
};

// eslint-disable-next-line react/prop-types
const PlayerPortalProvider = ({ children }) => {
    const [state, dispatch] = useReducer(middleware(reducer), initialState);
    return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

// eslint-disable-next-line react/prop-types
const DugoutProvider = ({ Team, children }) => {
    initialState.team = Team;
    const [state, dispatch] = useReducer(middleware(reducer), initialState);
    return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

export {
    store, actions, PlayerPortalProvider, DugoutProvider, setDugoutInitialContext,
};
